import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowRight, Sparkles, ChevronLeft, ChevronRight, Calendar, Instagram, ExternalLink } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { mockServices } from '../data/mock';
import useEmblaCarousel from 'embla-carousel-react';
import Autoplay from 'embla-carousel-autoplay';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const INSTAGRAM_URL = 'https://www.instagram.com/enrigo_us';

const Home = () => {
  const navigate = useNavigate();
  const [clients, setClients] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [instaLoaded, setInstaLoaded] = useState(false);

  // Blog carousel
  const autoplayPlugin = React.useRef(
    Autoplay({ delay: 4000, stopOnInteraction: false, stopOnMouseEnter: true })
  );
  const [emblaRef, emblaApi] = useEmblaCarousel(
    { loop: true, align: 'start', slidesToScroll: 1 },
    [autoplayPlugin.current]
  );

  const scrollPrev = useCallback(() => emblaApi && emblaApi.scrollPrev(), [emblaApi]);
  const scrollNext = useCallback(() => emblaApi && emblaApi.scrollNext(), [emblaApi]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [clientsRes, blogRes] = await Promise.all([
        axios.get(`${API}/clients`),
        axios.get(`${API}/blog`)
      ]);
      setClients(clientsRes.data.slice(0, 6));
      setBlogPosts(blogRes.data.slice(0, 6));
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Scroll to top of target page
  const navigateToTop = (path) => {
    navigate(path);
    window.scrollTo(0, 0);
  };

  return (
    <div className="bg-black text-white">
      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-black via-black to-gray-900 opacity-50"></div>
        <div className="container mx-auto px-6 relative z-10">
          <div className="max-w-5xl mx-auto text-center">
            <div className="mb-8">
              <img 
                src="https://customer-assets.emergentagent.com/job_enrigo-creative/artifacts/19zqfisl_EA%20Logo.png" 
                alt="Enrigo" 
                className="h-32 w-auto mx-auto mb-8 opacity-90"
              />
            </div>
            <h1 className="text-5xl md:text-7xl font-bold mb-6 tracking-tight">
              Creative Solutions for
              <span className="text-[#F4B400]"> Modern Brands</span>
            </h1>
            <p className="text-xl md:text-2xl text-gray-300 mb-12 leading-relaxed max-w-3xl mx-auto">
              10+ years transforming brands through strategic design, web development, and creative innovation
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg"
                onClick={() => navigateToTop('/portfolio')}
                className="bg-[#F4B400] text-black hover:bg-[#d49f00] text-lg px-8 py-6 transition-all duration-300"
              >
                View Portfolio
                <ArrowRight className="ml-2" size={20} />
              </Button>
              <Button 
                size="lg"
                variant="outline"
                onClick={() => navigateToTop('/shop')}
                className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black text-lg px-8 py-6 transition-all duration-300"
              >
                Our Services
              </Button>
            </div>
          </div>
        </div>
      </section>

      {/* What I Do Section */}
      <section className="py-24 bg-white text-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4">What I Do</h2>
            <p className="text-gray-600 text-lg max-w-2xl mx-auto">
              Comprehensive creative services tailored to elevate your brand
            </p>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
            {mockServices.map((service) => {
              const IconComponent = require('lucide-react')[service.icon];
              return (
                <Card 
                  key={service.id} 
                  className="group hover:shadow-2xl transition-all duration-300 border-2 hover:border-[#F4B400] cursor-pointer"
                  onClick={() => navigateToTop('/shop')}
                >
                  <CardContent className="p-8">
                    <div className="bg-[#F4B400] w-16 h-16 rounded-lg flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300">
                      <IconComponent className="text-black" size={32} />
                    </div>
                    <h3 className="text-2xl font-bold mb-3">{service.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{service.description}</p>
                  </CardContent>
                </Card>
              );
            })}
          </div>
        </div>
      </section>

      {/* Featured Projects - Dynamic from API */}
      <section className="py-24 bg-black">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold mb-4 text-white">Featured Work</h2>
            <p className="text-gray-400 text-lg max-w-2xl mx-auto">
              A selection of recent projects showcasing creativity and precision
            </p>
          </div>
          
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#F4B400] mx-auto"></div>
            </div>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
              {clients.map((client) => (
                <Card 
                  key={client._id}
                  className="group cursor-pointer overflow-hidden bg-white hover:shadow-2xl transition-all duration-300"
                  onClick={() => navigateToTop(`/portfolio/${client.slug}`)}
                >
                  <div className="relative overflow-hidden">
                    <img 
                      src={client.coverImage || 'https://via.placeholder.com/400x300?text=No+Image'} 
                      alt={client.clientName}
                      className="w-full h-64 object-cover group-hover:scale-110 transition-transform duration-500"
                    />
                    <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-60 transition-all duration-300 flex items-center justify-center">
                      <span className="text-[#F4B400] font-semibold opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                        View Project
                      </span>
                    </div>
                  </div>
                  <CardContent className="p-6">
                    <span className="text-[#F4B400] text-sm font-semibold">{client.year}</span>
                    <h3 className="text-xl font-bold mt-2 mb-2">{client.clientName}</h3>
                    <p className="text-gray-600 text-sm line-clamp-2">{client.description}</p>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
          
          <div className="text-center mt-12">
            <Button
              onClick={() => navigateToTop('/portfolio')}
              size="lg"
              variant="outline"
              className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-black transition-all duration-300"
            >
              View All Projects
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      {/* Latest Blog Posts Carousel */}
      {blogPosts.length > 0 && (
        <section className="py-24 bg-white text-black">
          <div className="container mx-auto px-6">
            <div className="text-center mb-16">
              <h2 className="text-4xl md:text-5xl font-bold mb-4">Latest from the Blog</h2>
              <p className="text-gray-600 text-lg max-w-2xl mx-auto">
                Insights, tutorials, and thoughts on design and technology
              </p>
            </div>
            
            <div className="relative">
              <div className="overflow-hidden" ref={emblaRef}>
                <div className="flex gap-6">
                  {blogPosts.map((post) => (
                    <div 
                      key={post._id} 
                      className="flex-none w-[350px] md:w-[400px]"
                    >
                      <Card 
                        className="h-full overflow-hidden hover:shadow-2xl transition-all duration-300 group cursor-pointer border-2 hover:border-[#F4B400]"
                        onClick={() => navigateToTop(`/blog/${post.slug}`)}
                      >
                        <div className="relative overflow-hidden">
                          <img 
                            src={post.featuredImage || 'https://customer-assets.emergentagent.com/job_enrigo-creative/artifacts/19zqfisl_EA%20Logo.png'} 
                            alt={post.title}
                            className="w-full h-52 object-cover group-hover:scale-110 transition-transform duration-500"
                          />
                          <Badge className="absolute top-3 left-3 bg-[#F4B400] text-black">
                            {post.category}
                          </Badge>
                        </div>
                        <CardContent className="p-6">
                          <div className="flex items-center text-gray-500 text-sm mb-3">
                            <Calendar size={14} className="mr-2" />
                            {new Date(post.publishedAt || post.createdAt).toLocaleDateString('en-US', { 
                              year: 'numeric', 
                              month: 'short', 
                              day: 'numeric' 
                            })}
                          </div>
                          <h3 className="text-xl font-bold mb-3 line-clamp-2 group-hover:text-[#F4B400] transition-colors">
                            {post.title}
                          </h3>
                          <p className="text-gray-600 text-sm line-clamp-3 leading-relaxed">
                            {post.excerpt || post.content?.substring(0, 150)}
                          </p>
                          <div className="mt-4 flex items-center text-[#F4B400] font-semibold text-sm group-hover:underline">
                            Read More <ArrowRight size={16} className="ml-2" />
                          </div>
                        </CardContent>
                      </Card>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* Navigation Buttons */}
              {blogPosts.length > 2 && (
                <>
                  <Button
                    onClick={scrollPrev}
                    className="absolute left-0 top-1/2 -translate-y-1/2 -translate-x-4 bg-black text-[#F4B400] border border-[#F4B400] hover:bg-[#F4B400] hover:text-black rounded-full w-12 h-12 p-0 shadow-lg z-10"
                    aria-label="Previous post"
                  >
                    <ChevronLeft size={24} />
                  </Button>
                  <Button
                    onClick={scrollNext}
                    className="absolute right-0 top-1/2 -translate-y-1/2 translate-x-4 bg-black text-[#F4B400] border border-[#F4B400] hover:bg-[#F4B400] hover:text-black rounded-full w-12 h-12 p-0 shadow-lg z-10"
                    aria-label="Next post"
                  >
                    <ChevronRight size={24} />
                  </Button>
                </>
              )}
            </div>
            
            <div className="text-center mt-12">
              <Button
                onClick={() => navigateToTop('/blog')}
                size="lg"
                variant="outline"
                className="border-[#F4B400] text-[#F4B400] hover:bg-[#F4B400] hover:text-white transition-all duration-300"
              >
                View All Posts
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
          </div>
        </section>
      )}

      {/* Instagram Live Feed Section */}
      <section className="py-24 bg-black relative overflow-hidden" data-testid="home-instagram-section">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent via-purple-900/5 to-transparent pointer-events-none" />
        <div className="container mx-auto px-6 relative z-10">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between mb-12">
            <div>
              <div className="flex items-center gap-3 mb-4">
                <div className="w-10 h-10 rounded-xl bg-gradient-to-tr from-yellow-400 via-pink-500 to-purple-600 flex items-center justify-center">
                  <Instagram className="text-white" size={20} />
                </div>
                <span className="text-sm font-semibold text-gray-500 tracking-widest uppercase">Instagram</span>
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-white mb-2">Follow Our Journey</h2>
              <p className="text-gray-400 max-w-md">Behind-the-scenes, latest projects, and creative inspiration — updated live from our feed.</p>
            </div>
            <div className="flex gap-3 mt-6 md:mt-0">
              <a href={INSTAGRAM_URL} target="_blank" rel="noopener noreferrer">
                <Button className="bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 text-white hover:opacity-90 transition-opacity" data-testid="home-instagram-follow-btn">
                  <Instagram size={16} className="mr-2" /> @enrigo_us
                </Button>
              </a>
              <Button
                variant="outline"
                onClick={() => navigateToTop('/social')}
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="home-view-social-btn"
              >
                View All <ArrowRight size={16} className="ml-2" />
              </Button>
            </div>
          </div>

          {/* Instagram Embed - Auto-updating */}
          <div className="relative max-w-2xl mx-auto" data-testid="home-instagram-embed">
            {!instaLoaded && (
              <div className="absolute inset-0 flex items-center justify-center bg-gray-900 rounded-2xl z-10">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-pink-500 mx-auto mb-3"></div>
                  <p className="text-gray-400 text-sm">Loading Instagram feed...</p>
                </div>
              </div>
            )}
            <div className="rounded-2xl overflow-hidden bg-white shadow-2xl shadow-purple-900/20 border border-white/10">
              <iframe
                src="https://www.instagram.com/enrigo_us/embed"
                className="w-full border-0"
                style={{ minHeight: '480px' }}
                scrolling="no"
                allowTransparency="true"
                loading="lazy"
                title="Instagram Feed @enrigo_us"
                onLoad={() => setInstaLoaded(true)}
              />
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 bg-white text-black">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <Sparkles className="text-[#F4B400] mx-auto mb-6" size={48} />
            <h2 className="text-4xl md:text-5xl font-bold mb-6">
              Let's Create Something Amazing Together
            </h2>
            <p className="text-gray-600 text-lg mb-8 leading-relaxed">
              Ready to transform your brand? Get in touch and let's discuss your project.
            </p>
            <Button
              onClick={() => navigateToTop('/contact')}
              size="lg"
              className="bg-[#F4B400] text-black hover:bg-[#d49f00] text-lg px-8 py-6 transition-all duration-300"
            >
              Get Started
              <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Home;
